import { useMutation } from '@platform/api-client';
import { useAuth } from '@platform/auth-client';
import { Button, formatNumber, PageHeader, Skeleton } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { BarChart3, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useTenantDetail } from '../catalog/useCatalogQueries.ts';
import { performanceQueryOf } from '../dashboard/performance.ts';
import { FINANCE_READ_ROLE } from '../finance/finance.ts';
import { LoadFailed } from '../LoadFailed.tsx';
import { recentDaysPeriodOf } from '../report-period.ts';
import { usePageContext } from '../usePageContext.ts';
import { CalculatorInputs } from './CalculatorInputs.tsx';
import { CalculatorResults } from './CalculatorResults.tsx';
import { DEFAULT_INPUT, parsedInputOf, type CalculatorField, type CalculatorInput } from './profitability.ts';
import { ProductStart, type StartingPrices } from './ProductStart.tsx';
import { REAL_RATES_DAYS, realRatesOf, type RealRates } from './real-rates.ts';
import styles from './Calculator.module.css';

const RATE_DECIMALS = 1;

export const CalculatorPage = ({ api }: { api: TypedClient }) => {
  const { t, locale, tenantId, queryClient, describeError } = usePageContext();
  const mayReadRates = useAuth().hasRole(FINANCE_READ_ROLE);
  const tenant = useTenantDetail(api);
  const [input, setInput] = useState<CalculatorInput>(DEFAULT_INPUT);
  const [ratesNotice, setRatesNotice] = useState<string | null>(null);
  const rateText = (rate: number) => formatNumber(rate, locale, RATE_DECIMALS);
  const keepOrReplace = (rate: number | null, typed: string) => (rate === null ? typed : rateText(rate));

  const applyRates = (rates: RealRates) => {
    setInput((current) => ({
      ...current,
      confirmationRate: keepOrReplace(rates.confirmationRate, current.confirmationRate),
      deliveryRate: keepOrReplace(rates.deliveryRate, current.deliveryRate),
    }));
    setRatesNotice(
      rates.confirmationRate === null || rates.deliveryRate === null
        ? t('calculator.realRatesMissing')
        : t('calculator.realRatesApplied', {
            confirmation: rateText(rates.confirmationRate),
            delivery: rateText(rates.deliveryRate),
          }),
    );
  };

  const realRates = useMutation({
    mutationFn: async () =>
      realRatesOf(
        await queryClient.query(
          performanceQueryOf({
            api,
            tenantId,
            period: recentDaysPeriodOf(REAL_RATES_DAYS, new Date()),
            unit: 'day',
            company: null,
          }),
        ),
      ),
    onSuccess: applyRates,
  });

  const change = (field: CalculatorField, value: string) => {
    setInput((current) => ({ ...current, [field]: value }));
  };
  const startFrom = (prices: StartingPrices) => {
    setInput((current) => ({ ...current, price: prices.price, cost: prices.cost ?? current.cost }));
  };

  const body = () => {
    if (tenant.isError)
      return <LoadFailed description={describeError(tenant.error)} onRetry={() => void tenant.refetch()} />;
    if (tenant.data === undefined) return <Skeleton height="480px" label={t('calculator.title')} />;
    return (
      <div className={styles['layout']}>
        <CalculatorInputs
          input={input}
          currency={tenant.data.currency}
          onChange={change}
          extras={{
            product: <ProductStart api={api} onPick={startFrom} />,
            rates: (
              <p className={realRates.isError ? styles['danger'] : styles['notice']} role="status">
                {realRates.isError ? describeError(realRates.error) : ratesNotice}
              </p>
            ),
          }}
        />
        <CalculatorResults input={parsedInputOf(input)} currency={tenant.data.currency} />
      </div>
    );
  };

  return (
    <>
      <PageHeader
        title={t('calculator.title')}
        context={t('calculator.context')}
        action={
          <div className={styles['actions']}>
            <Button
              variant="ghost"
              icon={<RotateCcw size={20} aria-hidden="true" />}
              onClick={() => {
                setInput(DEFAULT_INPUT);
                setRatesNotice(null);
              }}
            >
              {t('calculator.reset')}
            </Button>
            {mayReadRates ? (
              <Button
                variant="outline"
                icon={<BarChart3 size={20} aria-hidden="true" />}
                disabled={realRates.isPending}
                aria-busy={realRates.isPending}
                onClick={() => {
                  realRates.mutate();
                }}
              >
                {t('calculator.useRealRates')}
              </Button>
            ) : null}
          </div>
        }
      />
      {body()}
    </>
  );
};
